/**
 * TanStack Query hooks for Skills.
 *
 * Covers:
 * - Skill CRUD queries/mutations
 * - Activation toggle
 * - Query key management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  skillApi,
  type ListSkillsParams,
  type CreateSkillInput,
  type UpdateSkillInput,
  type SkillScope,
} from '@/services/skillApi';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const skillKeys = {
  all: ['skills'] as const,
  list: (params?: ListSkillsParams) => [...skillKeys.all, 'list', params] as const,
  detail: (id: string) => [...skillKeys.all, 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch skills list */
export function useSkills(params?: ListSkillsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: skillKeys.list(params),
    queryFn: () => skillApi.list(params),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single skill by ID */
export function useSkill(id: string | undefined) {
  return useQuery({
    queryKey: skillKeys.detail(id ?? ''),
    queryFn: () => skillApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new skill */
export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSkillInput) => skillApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
    },
  });
}

/** Update an existing skill */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSkillInput }) =>
      skillApi.update(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: skillKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
    },
  });
}

/** Delete a skill */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => skillApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
    },
  });
}

/** Duplicate a skill */
export function useDuplicateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input?: { new_name?: string; new_slug?: string; scope?: SkillScope } }) =>
      skillApi.duplicate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.all });
    },
  });
}

/** Activate skill for current user */
export function useActivateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => skillApi.activate(id),
    // Returned so mutateAsync resolves after the list refetch: the toggle keeps its new state
    // instead of flickering back to the stale is_activated_by_current_user.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: skillKeys.all }),
  });
}

/** Deactivate skill for current user */
export function useDeactivateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => skillApi.deactivate(id),
    // Returned so mutateAsync resolves after the list refetch: the toggle keeps its new state
    // instead of flickering back to the stale is_activated_by_current_user.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: skillKeys.all }),
  });
}
